import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { seekerDataService } from '../../services/seekerDataService';
import { type Guide, type HomePageData, type Session } from '../../types';
import { getBrowserTimezone, getTimezoneAbbreviation } from '../../utils/dateUtils';
import TestAVModal from './TestAVModal';

const DEFAULT_GUIDE_AVATAR =
  'http://localhost:3845/assets/ab29c58d5d0211dcf49eb600497756c2bd84e2e2.svg';

type GuideSummary = {
  name?: string;
  image?: string | null;
};

type AvatarProps = {
  src?: string;
  alt: string;
  fallback?: string;
  size?: number;
};

function Avatar({ src, alt, fallback, size = 32 }: AvatarProps) {
  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/40 bg-[#E8DFBD]"
      style={{ height: size, width: size }}
    >
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <span className="text-sm font-medium text-[#2C2731]">{fallback}</span>
      )}
    </div>
  );
}

function getInitials(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return 'GC';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatSessionTime(session: Session | null | undefined, seekerTimezone: string) {
  if (!session) return '';
  try {
    // Prefer start_time (UTC) if available - display in seeker's registered timezone
    if (session.start_time) {
      const timeStr = new Intl.DateTimeFormat('en-US', {
        timeZone: seekerTimezone,
        hour: 'numeric',
        minute: '2-digit'
      }).format(new Date(session.start_time));
      const tzAbbrev = getTimezoneAbbreviation(session.start_time, seekerTimezone);
      return tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;
    }

    // Legacy fallback - append Z since session_time is stored as UTC
    const start = new Date(`${session.session_date}T${session.session_time}Z`);
    return start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return session.session_time;
  }
}

function formatSessionDate(session: Session | null | undefined, seekerTimezone: string) {
  if (!session) return '';
  try {
    // Prefer start_time (UTC) if available - display in seeker's registered timezone
    if (session.start_time) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: seekerTimezone,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(session.start_time));
    }

    // Legacy fallback - append Z since session_time is stored as UTC
    const start = new Date(`${session.session_date}T${session.session_time}Z`);
    return start.toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return session.session_date;
  }
}

type MeetingWaitingRoomContentProps = {
  guide: Guide | null;
  session: Session;
};

const MeetingWaitingRoomContent: React.FC<MeetingWaitingRoomContentProps> = ({ guide, session }) => {
  const navigate = useNavigate();
  const [microphoneOn, setMicrophoneOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioDeviceIdRef = useRef<string | undefined>(undefined);
  const videoDeviceIdRef = useRef<string | undefined>(undefined);
  const [devicePrefsLoaded, setDevicePrefsLoaded] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [seekerTimezone, setSeekerTimezone] = useState<string>(getBrowserTimezone());

  // Load seeker's registered timezone, fallback to browser timezone
  useEffect(() => {
    const loadTimezone = async () => {
      const tz = await seekerDataService.getSeekerTimezone();
      setSeekerTimezone(tz || getBrowserTimezone());
    };
    loadTimezone();
  }, []);

  useEffect(() => {
    audioDeviceIdRef.current = localStorage.getItem('gc.audioDeviceId') || undefined;
    videoDeviceIdRef.current = localStorage.getItem('gc.videoDeviceId') || undefined;
    setDevicePrefsLoaded(true);
  }, []);

  useEffect(() => {
    if (!devicePrefsLoaded) return;
    let mounted = true;
    const startPreview = async () => {
      try {
        const constraints: MediaStreamConstraints = {
          audio: audioDeviceIdRef.current
            ? { deviceId: { ideal: audioDeviceIdRef.current } }
            : true,
          video: videoDeviceIdRef.current
            ? { deviceId: { ideal: videoDeviceIdRef.current } }
            : { facingMode: 'user' }
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const videoElement = videoRef.current;
        if (videoElement) {
          videoElement.srcObject = stream;
          videoElement.muted = true;
          try {
            await videoElement.play();
          } catch (err) {
            console.warn('[waiting-room] autoplay prevented', err);
          }
        }
        const audioTrack = stream.getAudioTracks()[0];
        const videoTrack = stream.getVideoTracks()[0];
        if (audioTrack) {
          setMicrophoneOn(audioTrack.enabled);
          const audioDeviceId = audioTrack.getSettings().deviceId as string | undefined;
          if (audioDeviceId) audioDeviceIdRef.current = audioDeviceId;
        }
        if (videoTrack) {
          setCameraOn(videoTrack.enabled);
          const videoDeviceId = videoTrack.getSettings().deviceId as string | undefined;
          if (videoDeviceId) videoDeviceIdRef.current = videoDeviceId;
        }
      } catch (err) {
        console.error('[waiting-room] getUserMedia failed', err);
      }
    };
    startPreview();
    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [devicePrefsLoaded]);

  useEffect(() => {
    const audioTrack = streamRef.current?.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = microphoneOn;
    }
  }, [microphoneOn]);

  useEffect(() => {
    const videoTrack = streamRef.current?.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = cameraOn;
    }
  }, [cameraOn]);

  const stopPreview = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const handleJoin = () => {
    if (audioDeviceIdRef.current) {
      localStorage.setItem('gc.audioDeviceId', audioDeviceIdRef.current);
    }
    if (videoDeviceIdRef.current) {
      localStorage.setItem('gc.videoDeviceId', videoDeviceIdRef.current);
    }
    localStorage.setItem('gc.microphoneEnabled', microphoneOn ? 'true' : 'false');
    localStorage.setItem('gc.cameraEnabled', cameraOn ? 'true' : 'false');
    stopPreview();
    navigate('/home/meeting');
  };

  const handleLeave = () => {
    stopPreview();
    navigate('/home');
  };

  const handleTestAV = () => {
    setShowTestModal(true);
  };

  const handleTestModalClose = () => {
    setShowTestModal(false);
  };

  const handleTestModalConfirm = (selection: {
    micId: string | undefined;
    camId: string | undefined;
    speakerId: string | undefined;
  }) => {
    // Update device preferences
    if (selection.micId) {
      audioDeviceIdRef.current = selection.micId;
      localStorage.setItem('gc.audioDeviceId', selection.micId);
    }
    if (selection.camId) {
      videoDeviceIdRef.current = selection.camId;
      localStorage.setItem('gc.videoDeviceId', selection.camId);
    }
    if (selection.speakerId) {
      localStorage.setItem('gc.speakerDeviceId', selection.speakerId);
    }
    setShowTestModal(false);
    // Restart preview with new devices
    setDevicePrefsLoaded(false);
    setTimeout(() => setDevicePrefsLoaded(true), 100);
  };

  const guideSummary: GuideSummary | null = useMemo(() => {
    if (guide) {
      return {
        name: guide.name,
        image: guide.image ?? null
      };
    }

    if (session.guide_name) {
      return { name: session.guide_name };
    }

    return null;
  }, [guide, session]);

  const guideName = guideSummary?.name ?? 'Your Guide';
  const guideInitials = getInitials(guideName);
  const guideAvatar = guideSummary?.image ? guideSummary.image : DEFAULT_GUIDE_AVATAR;

  const sessionTypeLabel = useMemo(() => {
    switch (session.session_type) {
      case 'preparation':
        return 'Preparation Session';
      case 'integration':
        return 'Integration Session';
      case 'journey':
        return 'Guided Journey';
      case 'follow_up':
        return 'Follow-up Session';
      case 'consultation':
      default:
        return 'Consultation Session';
    }
  }, [session.session_type]);

  const sessionTime = formatSessionTime(session, seekerTimezone);
  const sessionDate = formatSessionDate(session, seekerTimezone);

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            aria-label="Back to Home"
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
            onClick={handleLeave}
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10 flex items-center justify-end">
            <Avatar src={guideAvatar} alt={guideName} fallback={guideInitials} size={40} />
          </div>
        </header>

        <main className="flex flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6 lg:flex-row">
            {/* Left Column - Camera Preview & AV Controls */}
            <div className="flex flex-col gap-6 lg:w-1/2">
              <div className="relative overflow-hidden rounded-2xl bg-[#2C2731] px-6 py-10 text-center text-[#FFFCEF] shadow-sm">
                <div className="mx-auto mb-6 w-full overflow-hidden rounded-lg bg-[#1F1924]" style={{ height: '300px' }}>
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    controls={false}
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="text-sm font-medium text-[#FFFCEF]/80">Camera Preview</p>
              </div>

              <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
            <h3 className="text-base font-semibold text-[#2C2731]">Audio &amp; Video Settings</h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm font-medium text-[#2C2731]/80">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 8.5C14 10.9853 11.9853 13 9.5 13C7.01472 13 5 10.9853 5 8.5V5.5C5 3.01472 7.01472 1 9.5 1C11.9853 1 14 3.01472 14 5.5V8.5Z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <path
                      d="M2.5 9.5C2.5 12.8137 5.18629 15.5 8.5 15.5H10.5C13.8137 15.5 16.5 12.8137 16.5 9.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  Microphone
                </div>
                <button
                  type="button"
                  onClick={() => setMicrophoneOn((prev) => !prev)}
                  className={`relative flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF] ${
                    microphoneOn ? 'bg-[#2C2731]' : 'bg-[#E8DFBD]'
                  }`}
                  aria-label={microphoneOn ? 'Microphone on' : 'Microphone off'}
                  aria-pressed={microphoneOn}
                >
                  <span
                    className={`absolute h-5 w-5 rounded-full bg-[#FFFCEF] transition-all ${
                      microphoneOn ? 'left-6 translate-x-[-100%]' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm font-medium text-[#2C2731]/80">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 5C4 3.34315 5.34315 2 7 2H11C12.6569 2 14 3.34315 14 5V11C14 12.6569 12.6569 14 11 14H7C5.34315 14 4 12.6569 4 11V5Z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <path
                      d="M16 7L18 5V15L16 13"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Camera
                </div>
                <button
                  type="button"
                  onClick={() => setCameraOn((prev) => !prev)}
                  className={`relative flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF] ${
                    cameraOn ? 'bg-[#2C2731]' : 'bg-[#E8DFBD]'
                  }`}
                  aria-label={cameraOn ? 'Camera on' : 'Camera off'}
                  aria-pressed={cameraOn}
                >
                  <span
                    className={`absolute h-5 w-5 rounded-full bg-[#FFFCEF] transition-all ${
                      cameraOn ? 'left-6 translate-x-[-100%]' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm font-medium text-[#2C2731]/80">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 8C4 5.79086 5.79086 4 8 4H12C14.2091 4 16 5.79086 16 8V12C16 14.2091 14.2091 16 12 16H8C5.79086 16 4 14.2091 4 12V8Z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <path
                      d="M2 10H4M16 10H18"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  Speaker
                </div>
                <button
                  type="button"
                  onClick={() => setSpeakerOn((prev) => !prev)}
                  className={`relative flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF] ${
                    speakerOn ? 'bg-[#2C2731]' : 'bg-[#E8DFBD]'
                  }`}
                  aria-label={speakerOn ? 'Speaker on' : 'Speaker off'}
                  aria-pressed={speakerOn}
                >
                  <span
                    className={`absolute h-5 w-5 rounded-full bg-[#FFFCEF] transition-all ${
                      speakerOn ? 'left-6 translate-x-[-100%]' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </section>
            </div>

            {/* Right Column - Session Details & Actions */}
            <div className="flex flex-col gap-6 lg:w-1/2">
              <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-[#2C2731]">Upcoming Session</h2>
                <div className="mt-4 space-y-3 text-sm text-[#2C2731]/80">
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731]/70">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 6V4C14 3.44772 13.5523 3 13 3H7C6.44772 3 6 3.44772 6 4V6M3 8H17M5 10H5.01M10 10H10.01M15 10H15.01M5 14H5.01M10 14H10.01M15 14H15.01M4 5H16C17.1046 5 18 5.89543 18 7V17C18 18.1046 17.1046 19 16 19H4C2.89543 19 2 18.1046 2 17V7C2 5.89543 2.89543 5 4 5Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="font-medium text-[#2C2731] ph-mask">{guideName}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731]/70">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 6V10L12.5 11.5M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span>{sessionTime}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731]/70">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 1V4M15 1V4M3 7H17M4 3H16C17.1046 3 18 3.89543 18 5V17C18 18.1046 17.1046 19 16 19H4C2.89543 19 2 18.1046 2 17V5C2 3.89543 2.89543 3 4 3Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span>{sessionDate}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731]/70">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 10L18 8V12L15 10ZM2 6C2 5.44772 2.44772 5 3 5H11C11.5523 5 12 5.44772 12 6V14C12 14.5523 11.5523 15 11 15H3C2.44772 15 2 14.5523 2 14V6Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-[#2C2731]">{sessionTypeLabel}</span>
                  </div>
                </div>
              </section>

              <div className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] px-5 py-3 shadow-sm">
                <div className="flex items-center gap-3 text-sm text-[#2C2731]">
                  <span className="flex h-3 w-3 rounded-full bg-green-500" />
                  Connection: Excellent
                </div>
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-center rounded-lg bg-[#2C2731] px-4 py-3 text-sm font-medium text-[#FFFCEF] transition-colors hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
                  onClick={handleJoin}
                >
                  Join Session
                </button>
                <button
                  type="button"
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-4 py-3 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                  onClick={handleTestAV}
                >
                  Test Audio &amp; Video
                </button>
              </div>
            </div>
          </div>
        </main>

        <TestAVModal
          isOpen={showTestModal}
          onClose={handleTestModalClose}
          onConfirm={handleTestModalConfirm}
        />
      </div>
    </div>
  );
};

export const MeetingWaitingRoom: React.FC = () => {
  const navigate = useNavigate();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const data: HomePageData = await seekerDataService.getHomePageData();
        if (!mounted) return;
        setGuide(data.match?.guide_details ?? null);
        setSession(data.upcomingSession ?? null);
        setError(null);
      } catch (err) {
        if (!mounted) return;
        const message =
          err instanceof Error ? err.message : 'We could not load your waiting room details.';
        setError(message);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
              <p className="text-[#2C2731]/70">Preparing your waiting room…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <p className="mb-4 text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FFFCEF] transition-colors hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
              >
                Back to Home
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <p className="mb-4 text-sm text-[#2C2731]/70">
                You don&apos;t have a scheduled consultation right now. We&apos;ll be in touch when it&apos;s time to
                meet with your guide.
              </p>
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FFFCEF] transition-colors hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
              >
                Back to Home
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return <MeetingWaitingRoomContent guide={guide} session={session} />;
};

export default MeetingWaitingRoom;
